/**
 * Disposal (Retention & Disposal) Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class DisposalService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    async getAll(params = {}) {
        try {
            const response = await withRetry(() => this.api.getAll(params), { maxRetries: 2 });
            return {
                success: true,
                data: response.data,
                pagination: response.pagination,
                uniqueValues: response.uniqueValues,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch disposal-run list');
        }
    }

    async getById(id) {
        try {
            const response = await withRetry(() => this.api.getById(id), { maxRetries: 2 });
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch disposal-run');
        }
    }

    async create(data) {
        try {
            const response = await this.api.create(data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to create disposal-run');
        }
    }

    async update(id, data) {
        try {
            const response = await this.api.update(id, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to update disposal-run');
        }
    }

    async delete(id) {
        try {
            const response = await this.api.delete(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete disposal-run');
        }
    }

    async getRecords(id) {
        try {
            const response = await this.api.getRecords(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch disposal records');
        }
    }

    async addRecord(id, data) {
        try {
            const response = await this.api.addRecord(id, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to add disposal record');
        }
    }

    async complete(id, data = {}) {
        try {
            const response = await this.api.complete(id, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to complete disposal-run');
        }
    }

    async getReport(id) {
        try {
            const blob = await this.api.getReport(id);
            return { success: true, data: blob };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch disposal report');
        }
    }

    async getPolicyDocument() {
        try {
            const blob = await this.api.getPolicyDocument();
            return { success: true, data: blob };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch retention policy document');
        }
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) throw error;
        if (error.statusCode || error.status) throw ApiError.fromResponse(error);
        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
